<template>
  <div class="p-2 rounded-xl min-h-screen">
    <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl p-3 mb-4 shadow-sm border border-gray-100">
      <h2 class="text-sm font-bold text-gray-700 mb-1">آمار و تحلیل محصولات</h2>
      <p class="text-xs text-gray-500">
        اطلاعات مارکتینگی هر محصول: بازدید، فروش ۹۰ روز اخیر، امتیاز، نظر و تعداد علاقه‌مندی.
      </p>
    </div>

    <!-- کارت‌های خلاصه -->
    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4" v-if="overview">
      <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl p-3 shadow-sm border border-gray-100">
        <div class="text-[11px] text-gray-500">محصولات منتشرشده</div>
        <div class="text-lg font-bold text-gray-800">{{ formatNumber(overview.published_count) }}</div>
      </div>
      <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl p-3 shadow-sm border border-gray-100">
        <div class="text-[11px] text-gray-500">محصولات ویژه</div>
        <div class="text-lg font-bold text-emerald-600">{{ formatNumber(overview.boosted_count) }}</div>
      </div>
      <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl p-3 shadow-sm border border-gray-100">
        <div class="text-[11px] text-gray-500">ناموجود</div>
        <div class="text-lg font-bold text-red-500">{{ formatNumber(overview.out_of_stock_count) }}</div>
      </div>
      <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl p-3 shadow-sm border border-gray-100">
        <div class="text-[11px] text-gray-500">بازدید ۷ روز اخیر</div>
        <div class="text-lg font-bold text-gray-800">{{ formatNumber(overview.views_7d) }}</div>
      </div>
      <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl p-3 shadow-sm border border-gray-100">
        <div class="text-[11px] text-gray-500">تعداد فروش ۳۰ روز اخیر</div>
        <div class="text-lg font-bold text-gray-800">{{ formatNumber(overview.sales_quantity_30d) }}</div>
      </div>
      <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl p-3 shadow-sm border border-gray-100">
        <div class="text-[11px] text-gray-500">درآمد ۳۰ روز اخیر (تومان)</div>
        <div class="text-lg font-bold text-gray-800">{{ formatNumber(overview.sales_revenue_30d) }}</div>
      </div>
    </div>

    <!-- فیلتر و مرتب‌سازی -->
    <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl p-3 mb-4 shadow-sm border border-gray-100">
      <div class="flex flex-wrap items-center gap-2">
        <input
            v-model="search"
            placeholder="جستجو در نام محصول"
            class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-52 text-right"
            @keyup.enter="applyFilters"
        >
        <button
            @click="applyFilters"
            class="px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg"
        >جستجو</button>

        <div class="flex items-center gap-1 mr-auto">
          <button
              v-for="opt in orderOptions"
              :key="opt.value"
              @click="setOrdering(opt.value)"
              class="px-3 py-1.5 text-xs rounded-lg border"
              :class="ordering === opt.value ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600'"
          >{{ opt.label }}</button>
        </div>

        <label class="flex items-center gap-1 text-xs text-gray-600 px-2 py-1.5 bg-gray-100 rounded-lg border cursor-pointer">
          <input type="checkbox" v-model="boostedOnly" @change="applyFilters">
          فقط محصولات ویژه
        </label>
      </div>
    </div>

    <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
      <table class="w-full text-right whitespace-nowrap">
        <thead class="bg-gray-50">
        <tr>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">تصویر</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">نام محصول</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">برند</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">قیمت</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">موجودی</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">بازدید</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">فروش (۹۰ روز)</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">درآمد (۹۰ روز)</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">امتیاز</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">نظرات</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">علاقه‌مندی</th>
          <th class="p-3 text-xs font-medium text-gray-500 uppercase">ویژه</th>
        </tr>
        </thead>
        <tbody>
        <tr v-if="loading">
          <td colspan="12" class="p-6 text-center text-xs text-gray-400">در حال بارگذاری...</td>
        </tr>
        <tr v-else-if="!products.length">
          <td colspan="12" class="p-6 text-center text-xs text-gray-400">محصولی پیدا نشد</td>
        </tr>
        <tr v-for="product in products" :key="product.id" class="border-t hover:bg-gray-50">
          <td class="p-2">
            <img :src="img_source(product.image)" class="w-10 h-10 object-cover rounded-lg" v-if="product.image">
          </td>
          <td class="p-2 text-xs text-gray-700">{{ product.name }}</td>
          <td class="p-2 text-xs text-gray-500">{{ product.brand_name }}</td>
          <td class="p-2 text-xs text-gray-500">{{ formatNumber(product.price) }}</td>
          <td class="p-2 text-xs" :class="product.inventory_count > 0 ? 'text-gray-500' : 'text-red-500'">{{ formatNumber(product.inventory_count) }}</td>
          <td class="p-2 text-xs text-gray-500">{{ formatNumber(product.view_count) }}</td>
          <td class="p-2 text-xs text-gray-500">{{ formatNumber(product.sales_quantity_90d) }}</td>
          <td class="p-2 text-xs text-gray-500">{{ formatNumber(product.sales_revenue_90d) }}</td>
          <td class="p-2 text-xs text-gray-500">
            <span v-if="product.rating_count">{{ product.avg_rating.toFixed(1) }} ({{ product.rating_count }})</span>
            <span v-else class="text-gray-300">—</span>
          </td>
          <td class="p-2 text-xs text-gray-500">{{ formatNumber(product.comment_count) }}</td>
          <td class="p-2 text-xs text-gray-500">{{ formatNumber(product.wishlist_count) }}</td>
          <td class="p-2 text-xs">
            <span v-if="product.boost_at" class="text-emerald-600">✓</span>
            <span v-else class="text-gray-300">—</span>
          </td>
        </tr>
        </tbody>
      </table>

      <div class="flex items-center justify-between p-3 border-t text-xs text-gray-500" v-if="totalProducts">
        <span>مجموع: {{ formatNumber(totalProducts) }}</span>
        <div class="flex items-center gap-2">
          <button
              class="px-2 py-1 rounded-lg border disabled:opacity-40"
              :disabled="currentPage <= 1"
              @click="currentPage--; fetchProducts()"
          >قبلی</button>
          <span>صفحه {{ currentPage }} از {{ totalPages }}</span>
          <button
              class="px-2 py-1 rounded-lg border disabled:opacity-40"
              :disabled="currentPage >= totalPages"
              @click="currentPage++; fetchProducts()"
          >بعدی</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ProductMarketingStats",
  data() {
    return {
      overview: null,
      products: [],
      loading: false,
      totalProducts: 0,
      currentPage: 1,
      itemsPerPage: 24,
      search: "",
      boostedOnly: false,
      ordering: "sales",
      orderOptions: [
        { value: "sales", label: "پرفروش‌ترین" },
        { value: "views", label: "پربازدیدترین" },
        { value: "rating", label: "بهترین امتیاز" },
        { value: "revenue", label: "بیشترین درآمد" },
      ],
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalProducts / this.itemsPerPage));
    },
  },
  created() {
    this.setDocumentTitle && this.setDocumentTitle("آمار و تحلیل محصولات");
    this.fetchOverview();
    this.fetchProducts();
  },
  methods: {
    formatNumber(val) {
      if (val === null || val === undefined) return "۰";
      return Number(val).toLocaleString("fa-IR");
    },
    setOrdering(value) {
      this.ordering = value;
      this.applyFilters();
    },
    applyFilters() {
      this.currentPage = 1;
      this.fetchProducts();
    },
    fetchOverview() {
      this.request({
        url: this.endpoint("product/adminProductMarketingOverview"),
        method: "get",
        success: (data) => {
          this.overview = data;
        },
      });
    },
    fetchProducts() {
      this.loading = true;
      const params = {
        limit: this.itemsPerPage,
        offset: (this.currentPage - 1) * this.itemsPerPage,
        ordering: this.ordering,
        search_value: this.search,
      };
      if (this.boostedOnly) params.boosted = true;

      this.request({
        url: this.endpoint("product/adminProductMarketingStats"),
        method: "get",
        params,
        success: (data) => {
          this.products = data.results || [];
          this.totalProducts = data.count || 0;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
    },
  },
};
</script>
